import React, { useMemo, useCallback } from 'react';
import { Drawer } from 'antd';
import type { DrawerProps as AntDrawerProps } from 'antd';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';

/**
 * AndDrawer Props 接口
 */
export interface AndDrawerProps extends Omit<AntDrawerProps, 'onClose'>, BaseComponentProps, DataSetConfig {
  /** 关闭动作 */
  closeAction?: string;
  /** 关闭回调 */
  onClose?: () => void;
}

/**
 * AndDrawer 组件
 * 封装 Ant Design Drawer，支持数据绑定和动作解析
 */
export const AndDrawer: React.FC<AndDrawerProps> = ({
  id,
  sessionId,
  develop,
  dataSet,
  dataSetField,
  closeAction,
  onClose,
  open: propOpen,
  children,
  ...restProps
}) => {
  const addCommand = useAndStore((state) => state.addCommand);
  const getDataSet = useAndStore((state) => state.getDataSet);
  const setDataSet = useAndStore((state) => state.setDataSet);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  // 从数据集获取 open 状态
  const storeOpen = useMemo(() => {
    if (dataSetKey && dataSetField) {
      return getDataSet(dataSetKey, dataSetField);
    }
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  const displayOpen = storeOpen !== undefined ? Boolean(storeOpen) : propOpen;

  // 关闭处理
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }

    // 关闭 Drawer
    if (dataSetKey && dataSetField) {
      setDataSet(dataSetKey, dataSetField, false);
    }

    // 处理关闭动作
    if (closeAction && sessionId) {
      addCommand(sessionId, {
        actionName: closeAction,
        arg: null,
        timestamp: Date.now(),
      });
    }
  }, [onClose, dataSetKey, dataSetField, setDataSet, closeAction, sessionId, addCommand]);

  return (
    <Drawer
      {...restProps}
      open={displayOpen}
      onClose={handleClose}
    >
      {children}
    </Drawer>
  );
};

export default AndDrawer;
